import { Component, HostBinding } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Fixture } from '@cds/betting-offer';
import { CDNCachingConfig } from '@frontend/sports/common/client-config-data-access';
import { MarqueeBackgroundContainerConfig, TitleIconOptions } from '@frontend/sports/types/components/content';
import { WidgetContext, WidgetType } from '@frontend/sports/types/components/widget';
import { MarqueeLink, OutrightsMarqueePayload } from '@frontend/sports/types/components/widget/types';
import { Observable, filter, forkJoin, map } from 'rxjs';

import { WidgetContextService } from '../widget/common/widget-context.service';
import { WidgetComponent } from '../widget/core/widget-registry.service';
import { MarqueeTrackingService } from './marquee-tracking.service';

@Component({
    selector: 'ms-outrights-marquee-widget',
    templateUrl: './outrights-marquee-widget.component.html',
    providers: [MarqueeTrackingService],
    standalone: false,
})
export class OutrightsMarqueeWidgetComponent extends WidgetComponent<OutrightsMarqueePayload> {
    @HostBinding('class') className = 'outrights-marquee-widget';

    fixture: Fixture;
    backgroundContainerConfig: MarqueeBackgroundContainerConfig;
    globalLinkConfig: MarqueeLink;
    maxSelection?: number;
    private shouldIncludePayload = false;

    constructor(
        private widgetConetextService: WidgetContextService,
        private cdnCachingConfig: CDNCachingConfig,
    ) {
        super();
        this.shouldIncludePayload = this.cdnCachingConfig.widgets.includes(WidgetType.OutrightsMarquee);
    }

    protected override onData(data: OutrightsMarqueePayload): void {
        if (!data?.fixture) {
            forkJoin([this.getData(), this.getData(false, true)])
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe(([regularMarqueePayload, personalizedMarqueePayload]) => {
                    const combinedData = this.mergePayload(regularMarqueePayload, personalizedMarqueePayload);
                    this.renderData(combinedData);
                });
        } else {
            this.renderData(data);
        }
    }

    private mergePayload(
        regularMarqueePayload: OutrightsMarqueePayload,
        personalizedMarqueePayload: OutrightsMarqueePayload,
    ): OutrightsMarqueePayload {
        return regularMarqueePayload || personalizedMarqueePayload || ({} as OutrightsMarqueePayload);
    }

    renderData = (data: OutrightsMarqueePayload) => {
        if (data?.fixture) {
            this.fixture = data.fixture;
            this.hasData = !!this.fixture && !!data.maxSelections;
            if (this.hasData) {
                this.populateConfigData(data);
                this.maxSelection = data.maxSelections;
            }
        } else {
            this.hasData = false;
            this.logEmptyData();
        }
        this.setWidgetLoaded();
    };

    override getContext(): Partial<WidgetContext> {
        return { ...super.getContext(), shouldIncludePayload: this.shouldIncludePayload, marqueesOnly: true };
    }

    private getData(addSchemaMarkup: boolean = false, shouldIncludePersonalizedData: boolean = false): Observable<OutrightsMarqueePayload> {
        return this.widgetConetextService
            .getWidget<OutrightsMarqueePayload>({ ...this.getContext(), shouldIncludePersonalizedData }, addSchemaMarkup)
            .pipe(
                filter(({ payload }) => payload !== undefined || this.shouldIncludePayload),
                map(({ payload }) => payload as OutrightsMarqueePayload),
                takeUntilDestroyed(this.destroyRef),
            );
    }

    private populateConfigData(data: OutrightsMarqueePayload): void {
        this.backgroundContainerConfig = {
            showTitle: data.backgroundContainerConfig?.showTitle ?? false,
            title: data.backgroundContainerConfig?.title,
            titleIconDisplay: data.backgroundContainerConfig?.titleIconDisplay ?? TitleIconOptions.None,
            backgroundImage: data.backgroundContainerConfig?.backgroundImage,
            textColor: data.backgroundContainerConfig?.textColor,
        };
        this.globalLinkConfig = {
            linkOverride: data.globalLinkOverride ?? '',
            title: data.globalLinkTitle ?? '',
            visible: data.globalLinkVisible ?? '',
        };
    }
}
